import {useMemo, useState} from 'react'
import {Button, Form, Table} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {IMessageTemplate} from '../../../types/campaigns'
import {ChannelBadge} from './ChannelBadge'

type Props = {
  templates: IMessageTemplate[]
  onCreate: () => void
  onEdit: (template: IMessageTemplate) => void
}

const channelOptions: Array<{value: string; label: string}> = [
  {value: '', label: 'All channels'},
  {value: 'EMAIL', label: 'Email'},
  {value: 'SMS', label: 'SMS'},
  {value: 'WHATSAPP', label: 'WhatsApp'},
  {value: 'TELEGRAM', label: 'Telegram'},
]

export const TemplatesPanel = ({templates, onCreate, onEdit}: Props) => {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [channelFilter, setChannelFilter] = useState('')

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesChannel = !channelFilter || template.channel === channelFilter
      const text = `${template.name} ${template.description ?? ''} ${template.subject ?? ''}`.toLowerCase()
      const matchesSearch = !search || text.includes(search.toLowerCase())
      return matchesChannel && matchesSearch
    })
  }, [templates, channelFilter, search])

  return (
    <div className='card mt-5'>
      <div className='card-header border-0'>
        <div>
          <h4 className='mb-1'>Templates</h4>
          <span className='text-muted'>Central repository of message templates for every channel.</span>
        </div>
        <Button size='sm' onClick={onCreate}>SMS recibido de
          New template
        </Button>
      </div>
      <div className='card-body border-top'>
        <div className='row g-3 mb-4'>
          <div className='col-md-6'>
            <Form.Control
              type='search'
              placeholder='Search by name or content'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className='col-md-3'>
            <Form.Select value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
              {channelOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>
        {filteredTemplates.length === 0 ? (
          <div className='campaigns-placeholder justify-content-start px-0'>
            {templates.length === 0 ? 'No templates yet. Start by creating one.' : 'No templates match the selected filters.'}
          </div>
        ) : (
          <div className='table-responsive'>
            <Table hover className='align-middle mb-0'>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Channel</th>
                  <th>Subject</th>
                  <th>Variables</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id}>
                    <td>
                      <div className='fw-semibold'>{template.name}</div>
                      <div className='text-muted fs-8'>{template.description || 'No description'}</div>
                    </td>
                    <td>
                      <ChannelBadge channel={template.channel} />
                    </td>
                    <td>{template.subject || '—'}</td>
                    <td>
                      <div className='text-muted'>
                        {template.variables && (Array.isArray(template.variables) ? template.variables.length : 0) > 0
                          ? (Array.isArray(template.variables) ? template.variables.join(', ') : template.variables)
                          : '—'}
                      </div>
                    </td>
                    <td>{template.updatedAt ? new Date(template.updatedAt).toLocaleString() : '—'}</td>
                    <td className='text-end'>
                      <Button 
                        size='sm' 
                        variant='primary' 
                        onClick={() => navigate(`/templates/edit/${template.id}`)}
                        className='me-2'
                      >
                        📝 Variants
                      </Button>
                      <Button size='sm' variant='light' onClick={() => onEdit(template)}>
                        Edit Legacy
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
